/**
 * Library V2 Phase 3 — search-first Library explorer (spec §8–§10).
 * Search box + filter rail + facet counts + assessment cards.
 * Filters are URL-addressable (shareable deep links).
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Switch } from '@/components/ui/switch';
import { adminApi } from '@/lib/adminApi';
import { useDataLabel } from '@/lib/dataI18n';
import {
  Search, X, Loader2, Clock, ListChecks, Lock, SlidersHorizontal,
  ChevronLeft, ChevronRight, FileQuestion,
} from 'lucide-react';

const FILTER_KEYS = ['q', 'taxonomy', 'category', 'suite', 'language', 'level', 'assessment_type', 'status', 'use', 'competency', 'include_archived', 'page'];

const STATUS_TONE = {
  published: 'bg-success/10 text-success border-success/20',
  approved: 'bg-primary/10 text-primary border-primary/20',
  submitted_for_review: 'bg-info/10 text-info border-info/20',
  needs_revision: 'bg-warning/10 text-warning border-warning/20',
  archived: 'text-muted-foreground',
  draft: '',
};

export function LibrarySearchExplorer({ isAdmin }) {
  const { t, i18n } = useTranslation();
  const isES = (i18n.language || '').toLowerCase().startsWith('es');
  const tData = useDataLabel();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState({ items: [], total: 0, facets: {} });
  const [loading, setLoading] = useState(true);
  const [taxonomy, setTaxonomy] = useState([]);
  const [uses, setUses] = useState([]);
  const [comps, setComps] = useState([]);
  const [qInput, setQInput] = useState(params.get('q') || '');
  const debounceRef = useRef(null);

  const get = (k) => params.get(k) || '';
  const setFilter = (k, v) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v); else next.delete(k);
    if (k !== 'page') next.delete('page');
    if (k === 'taxonomy') next.delete('category');
    setParams(next, { replace: true });
  };
  const clearAll = () => {
    const next = new URLSearchParams(params);
    FILTER_KEYS.forEach((k) => next.delete(k));
    setParams(next, { replace: true });
    setQInput('');
  };

  useEffect(() => {
    Promise.all([
      adminApi.libraryTaxonomy().catch(() => []),
      adminApi.libraryRecommendedUses().catch(() => []),
      adminApi.listCompetencies(false).catch(() => []),
    ]).then(([tx, us, cs]) => { setTaxonomy(tx); setUses(us); setComps(cs); });
  }, []);

  useEffect(() => {
    setLoading(true);
    const req = {};
    FILTER_KEYS.forEach((k) => { const v = params.get(k); if (v) req[k] = v; });
    // domain + category cascade: category (deeper node) wins
    if (req.category) { req.taxonomy = req.category; delete req.category; }
    adminApi.librarySearch(req)
      .then(setData)
      .catch(() => setData({ items: [], total: 0, facets: {} }))
      .finally(() => setLoading(false));
  }, [params]);

  const onSearchChange = (v) => {
    setQInput(v);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setFilter('q', v.trim()), 350);
  };

  const label = (n) => (isES ? n.label_i18n?.es : n.label_i18n?.en) || n.slug;
  const domains = useMemo(() => taxonomy.filter((n) => n.level === 'domain'), [taxonomy]);
  const categories = useMemo(() => {
    const d = get('taxonomy');
    return d ? taxonomy.filter((n) => n.parent_id === d) : [];
  }, [taxonomy, params]);

  const facets = data.facets || {};
  const fc = (facet, key) => {
    const n = (facets[facet] || {})[key];
    return n ? ` (${n})` : '';
  };

  const page = parseInt(get('page') || '1', 10);
  const pageSize = data.page_size || 24;
  const pages = Math.max(1, Math.ceil((data.total || 0) / pageSize));

  const activeChips = useMemo(() => {
    const chips = [];
    const push = (k, lbl) => chips.push({ k, lbl });
    if (get('taxonomy')) { const n = taxonomy.find((x) => x.id === get('taxonomy')); if (n) push('taxonomy', label(n)); }
    if (get('category')) { const n = taxonomy.find((x) => x.id === get('category')); if (n) push('category', label(n)); }
    if (get('suite')) push('suite', get('suite') === 'internal_library' ? t('newEval.internalLibrary', { defaultValue: 'Biblioteca Interna' }) : get('suite'));
    if (get('language')) push('language', get('language').toUpperCase());
    if (get('level')) push('level', get('level'));
    if (get('assessment_type')) push('assessment_type', t(`library.typeBar.types.${get('assessment_type')}`, { defaultValue: t(`library.search.types.${get('assessment_type')}`, { defaultValue: get('assessment_type') }) }));
    if (get('status')) push('status', t(`library.lifecycle.statuses.${get('status')}`, { defaultValue: get('status') }));
    if (get('use')) { const u = uses.find((x) => x.id === get('use')); if (u) push('use', isES ? u.label_i18n?.es : u.label_i18n?.en); }
    if (get('competency')) { const c = comps.find((x) => x.code === get('competency')); push('competency', c?.name || get('competency')); }
    return chips;
  }, [params, taxonomy, uses, comps, isES]);

  const SUITES = ['talentmap', 'talentcheck', 'talentstart', 'climara', 'talentcompetency', 'talenttrain', 'talentheart', 'talentfix', 'intelligence'];
  const TYPES = ['psychometric', 'competency', 'knowledge', 'climate', 'certification', 'behavioral', '360', 'other'];
  const STATUSES = ['published', 'approved', 'submitted_for_review', 'needs_revision', 'draft'];

  return (
    <div className="space-y-4" data-testid="library-search-explorer">
      {/* Search box */}
      <Card className="p-4 shadow-card">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
          <Input
            data-testid="library-search-input"
            value={qInput}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder={t('library.search.placeholder', { defaultValue: 'Search assessments by name, topic, competency, taxonomy…' })}
            className="pl-10 h-11 text-base"
          />
          {qInput && (
            <button
              type="button"
              data-testid="library-search-clear"
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              onClick={() => onSearchChange('')}
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        {/* Primary filter rail */}
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <RailSelect testId="library-filter-domain" value={get('taxonomy')} onChange={(v) => setFilter('taxonomy', v)}
            placeholder={t('library.search.domain', { defaultValue: 'Domain' })}
            options={domains.map((d) => ({ value: d.id, label: `${label(d)}${fc('domain', d.id)}` }))} />
          {categories.length > 0 && (
            <RailSelect testId="library-filter-category" value={get('category')} onChange={(v) => setFilter('category', v)}
              placeholder={t('library.search.category', { defaultValue: 'Category' })}
              options={categories.map((c) => ({ value: c.id, label: label(c) }))} />
          )}
          <RailSelect testId="library-filter-suite" value={get('suite')} onChange={(v) => setFilter('suite', v)}
            placeholder={t('library.search.suite', { defaultValue: 'Suite' })}
            options={[...SUITES.map((s) => ({ value: s, label: `${s}${fc('suite', s)}` })),
              { value: 'internal_library', label: `${t('newEval.internalLibrary', { defaultValue: 'Biblioteca Interna' })}${fc('suite', 'internal_library')}` }]} />
          <RailSelect testId="library-filter-language" value={get('language')} onChange={(v) => setFilter('language', v)}
            placeholder={t('library.search.language', { defaultValue: 'Language' })}
            options={Object.keys(facets.language || { es: 1 }).map((l) => ({ value: l, label: `${l.toUpperCase()}${fc('language', l)}` }))} />
          <RailSelect testId="library-filter-type" value={get('assessment_type')} onChange={(v) => setFilter('assessment_type', v)}
            placeholder={t('library.search.type', { defaultValue: 'Type' })}
            options={TYPES.map((ty) => ({ value: ty, label: `${t(`library.typeBar.types.${ty}`, { defaultValue: t(`library.search.types.${ty}`, { defaultValue: ty }) })}${fc('assessment_type', ty)}` }))} />
          <RailSelect testId="library-filter-status" value={get('status') || 'published'}
            onChange={(v) => setFilter('status', v === 'published' ? '' : v)}
            placeholder={t('library.search.status', { defaultValue: 'Status' })}
            allValue="all"
            allLabel={t('library.search.allStatuses', { defaultValue: 'All statuses' })}
            options={STATUSES.map((s) => ({ value: s, label: `${t(`library.lifecycle.statuses.${s}`, { defaultValue: s })}${fc('status', s)}` }))} />

          {/* Secondary filters */}
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className="h-9 gap-1.5" data-testid="library-more-filters">
                <SlidersHorizontal className="h-3.5 w-3.5" /> {t('library.search.moreFilters', { defaultValue: 'More filters' })}
              </Button>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-72 p-4 space-y-3">
              <div>
                <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-1">
                  {t('library.search.recommendedUse', { defaultValue: 'Recommended use' })}
                </p>
                <RailSelect testId="library-filter-use" value={get('use')} onChange={(v) => setFilter('use', v)} wide
                  placeholder={t('library.search.recommendedUse', { defaultValue: 'Recommended use' })}
                  options={uses.map((u) => ({ value: u.id, label: isES ? u.label_i18n?.es : u.label_i18n?.en }))} />
              </div>
              <div>
                <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-1">
                  {t('library.search.competency', { defaultValue: 'Competency measured' })}
                </p>
                <RailSelect testId="library-filter-competency" value={get('competency')} onChange={(v) => setFilter('competency', v)} wide
                  placeholder={t('library.search.competency', { defaultValue: 'Competency' })}
                  options={comps.filter((c) => !c.archived).map((c) => ({ value: c.code, label: c.name }))} />
              </div>
              {isAdmin && (
                <div className="flex items-center justify-between pt-1 border-t">
                  <span className="text-xs text-muted-foreground">{t('library.search.includeArchived', { defaultValue: 'Include archived' })}</span>
                  <Switch
                    data-testid="library-include-archived"
                    checked={get('include_archived') === 'true'}
                    onCheckedChange={(v) => setFilter('include_archived', v ? 'true' : '')}
                  />
                </div>
              )}
            </PopoverContent>
          </Popover>

          <div className="ml-auto text-xs text-muted-foreground tabular-nums" data-testid="library-result-count">
            {t('library.filters.resultCount', { count: data.total, defaultValue: '{{count}} results' })}
          </div>
        </div>

        {/* Active filter chips */}
        {activeChips.length > 0 && (
          <div className="mt-3 flex flex-wrap items-center gap-1.5" data-testid="library-active-chips">
            {activeChips.map((c) => (
              <Badge key={c.k} variant="secondary" className="gap-1 pr-1">
                {c.lbl}
                <button type="button" data-testid={`library-chip-remove-${c.k}`} onClick={() => setFilter(c.k, '')} className="hover:text-destructive">
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            ))}
            <Button size="sm" variant="ghost" className="h-6 text-xs text-muted-foreground" onClick={clearAll} data-testid="library-clear-filters">
              {t('library.filters.reset', { defaultValue: 'Reset' })}
            </Button>
          </div>
        )}
      </Card>

      {/* Results */}
      {loading ? (
        <div className="p-12 flex items-center justify-center text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin mr-2" /> {t('pages.questionnaires.loading')}
        </div>
      ) : data.items.length === 0 ? (
        <Card className="p-12 text-center shadow-card" data-testid="library-empty-state">
          <FileQuestion className="h-10 w-10 text-muted-foreground/40 mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">
            {t('library.search.noResults', { defaultValue: 'No assessments match your search.' })}
          </p>
          {activeChips.length > 0 && (
            <Button size="sm" variant="outline" className="mt-3" onClick={() => setFilter(activeChips[activeChips.length - 1].k, '')}>
              {t('library.search.removeLastFilter', { defaultValue: 'Remove filter' })}: {activeChips[activeChips.length - 1].lbl}
            </Button>
          )}
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="library-cards-grid">
          {data.items.map((a) => (
            <Card
              key={a.id}
              data-testid={`library-card-${a.code}`}
              onClick={() => navigate(`/admin/library/${a.id}`)}
              className="p-4 shadow-card cursor-pointer transition-smooth hover:shadow-md hover:border-primary/30 flex flex-col gap-2"
            >
              <div className="flex items-start justify-between gap-2">
                <h3 className="font-display font-bold text-sm leading-snug">{tData(a.name)}</h3>
                <Badge variant="outline" className={`shrink-0 text-xs ${STATUS_TONE[a.status] || ''}`}>
                  {t(`library.lifecycle.statuses.${a.status}`, { defaultValue: a.status })}
                </Badge>
              </div>
              {a.taxonomy_path?.length > 0 && (
                <p className="text-xs text-muted-foreground">
                  {a.taxonomy_path.map((p) => (isES ? p.es : p.en)).join(' / ')}
                </p>
              )}
              <p className="text-xs text-muted-foreground line-clamp-2">{tData(a.description)}</p>
              <div className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground mt-auto pt-1">
                {a.assessment_type && (
                  <Badge variant="secondary" className="text-xs">
                    {t(`library.search.types.${a.assessment_type}`, { defaultValue: a.assessment_type })}
                  </Badge>
                )}
                {(a.config_languages || []).map((l) => (
                  <Badge key={l} variant="outline" className="text-xs uppercase">{l}</Badge>
                ))}
                {a.question_count != null && (
                  <span className="inline-flex items-center gap-1"><ListChecks className="h-3 w-3" />{a.question_count}</span>
                )}
                {a.estimated_duration_minutes && (
                  <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" />{a.estimated_duration_minutes} min</span>
                )}
              </div>
              <div className="flex flex-wrap gap-1">
                {(a.suite_codes || []).map((s) => (
                  <Badge key={s} variant="outline" className="text-xs gap-0.5">
                    {s}
                    {(a.exclusive_suites || []).includes(s) && <Lock className="h-2.5 w-2.5" />}
                  </Badge>
                ))}
                {(a.suite_codes || []).length === 0 && (
                  <Badge variant="outline" className="text-xs text-muted-foreground">
                    {t('newEval.internalLibrary', { defaultValue: 'Biblioteca Interna' })}
                  </Badge>
                )}
                {/* Founder UAT sprint (FIX 6A/7) — published != suite-ready */}
                {a.status === 'published' && (
                  a.configurations_count > 0 ? (
                    <Badge className="text-xs bg-success/10 text-success border-success/30" data-testid={`library-ready-${a.id}`}>
                      {t('library.readiness.operational', { defaultValue: 'Operativa ({{n}} config. activa)', n: a.configurations_count })}
                    </Badge>
                  ) : (
                    <Badge className="text-xs bg-warning/10 text-warning border-warning/30" data-testid={`library-noconfig-${a.id}`}>
                      {t('library.readiness.noActiveConfig', { defaultValue: 'Sin configuración activa — no asignable' })}
                    </Badge>
                  )
                )}
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Pagination */}
      {pages > 1 && (
        <div className="flex items-center justify-center gap-3" data-testid="library-pagination">
          <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setFilter('page', String(page - 1))} data-testid="library-page-prev">
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-xs text-muted-foreground tabular-nums">{page} / {pages}</span>
          <Button size="sm" variant="outline" disabled={page >= pages} onClick={() => setFilter('page', String(page + 1))} data-testid="library-page-next">
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      )}
    </div>
  );
}

function RailSelect({ value, onChange, options, placeholder, testId, allValue = '', allLabel, wide }) {
  const current = value || 'all';
  return (
    <Select value={current} onValueChange={(v) => onChange(v === 'all' ? (allValue === 'all' ? 'all' : '') : v)}>
      <SelectTrigger className={`h-9 ${wide ? 'w-full' : 'w-auto min-w-[150px] max-w-[260px] [&>span]:whitespace-nowrap'}`} data-testid={testId} aria-label={placeholder}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="all">{allLabel || placeholder}</SelectItem>
        {options.map((o) => (
          <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export default LibrarySearchExplorer;
